import {clsx} from 'clsx'
import {use} from 'react'
import type {Icon} from '@primer/octicons-react'
import type {MouseEvent, ReactNode} from 'react'
import {DisclosureChevron} from '../disclosure/DisclosureChevron'
import {DisclosureTrigger} from '../disclosure/DisclosureTrigger'
import {AccordionConfigContext, AccordionItemContext} from './accordion-context'
import styles from './Accordion.module.css'

export type AccordionHeadingLevel = 1 | 2 | 3 | 4 | 5 | 6

export interface AccordionHeaderProps {
  /**
   * Semantic heading level only, does not affect the visual size of the header.
   */
  headingLevel: AccordionHeadingLevel
  /**
   * Decorative icon before the label.
   */
  leadingVisual?: Icon
  className?: string
  children: ReactNode
}

const LEADING_CHEVRON_ROTATION = {closedRotation: -90, openRotation: 0}
const TRAILING_CHEVRON_ROTATION = {closedRotation: 0, openRotation: 180}

export const AccordionHeader = ({
  headingLevel,
  leadingVisual: LeadingVisual,
  className,
  children,
}: AccordionHeaderProps) => {
  const config = use(AccordionConfigContext)
  const item = use(AccordionItemContext)

  if (!config || !item) throw new Error('Accordion.Header must be rendered inside Accordion.Item')

  const {chevronPlacement} = config
  const isLeading = chevronPlacement === 'leading'
  const Heading = `h${headingLevel}` as const

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    // Accordion.Item `disabled` maps to aria-disabled, not the native attribute, so the
    // header stays tabbable. Block activation here instead.
    if (item.disabled) event.preventDefault()
  }

  const chevron = (
    <DisclosureChevron
      className={styles.Chevron}
      {...(isLeading ? LEADING_CHEVRON_ROTATION : TRAILING_CHEVRON_ROTATION)}
    />
  )

  return (
    <Heading className={styles.Heading}>
      <DisclosureTrigger
        render={
          <button
            type="button"
            aria-disabled={item.disabled || undefined}
            className={clsx(styles.Trigger, isLeading ? styles.leading : styles.trailing, className)}
            onClick={handleClick}
          />
        }
      >
        {isLeading && chevron}
        {LeadingVisual && (
          <span aria-hidden="true" className={styles.LeadingVisual}>
            <LeadingVisual />
          </span>
        )}
        <span className={styles.Label}>{children}</span>
        {!isLeading && chevron}
      </DisclosureTrigger>
    </Heading>
  )
}

AccordionHeader.displayName = 'Accordion.Header'
